import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal, CustomSelect } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { FLAG_OPTIONS } from "@constants/index";
import { LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION } from "@constants/openSource";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { isNotNullAndEmptyArray, uppercase } from "@utils/common";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { MagnifyingGlassIcon, PlusIcon } from "@heroicons/react/24/outline";
import { UnMatchIcon, MatchIcon } from "@components/Icons";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";

export default function LicenseList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState(searchFilters);
  const debounceTimerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const [licenseList, setLicenseList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.LICENSE_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const isInitialMount = useRef(true);
  const prevCurrentPage = useRef(1);

  const getListData = useMutationData(
    queryKeys.getListLicenses,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const list = response.object || [];
          const totalCount = response.objectCount || 0;
          setLicenseList(list);
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / (itemsPerPage || 1)));
        }
      },
      onError: (error) => {
        console.error("Error fetching licenses:", error);
        showErrorToast(t("open-source.get-license-list-error"));
      },
    },
  );

  const fetchData = useCallback(() => {
    if (getListData.isPending) {
      return;
    }

    const bodyRequest = Object.keys(debouncedSearchFilters).reduce((acc, key) => {
      let value = debouncedSearchFilters[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});

    bodyRequest.pageSize = itemsPerPage;
    bodyRequest.startRow = itemsPerPage * (currentPage - 1);

    getListData.mutate(bodyRequest);
  }, [debouncedSearchFilters, currentPage, itemsPerPage, getListData.isPending]);

  useEffect(() => {
    if (!isInitialMount.current && prevCurrentPage.current !== currentPage) {
      fetchData();
      prevCurrentPage.current = currentPage;
    }
  }, [currentPage]);

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const hasActualChange = Object.values(searchFilters).some((value) => value !== undefined && value !== null && value !== "");
    if (hasActualChange) {
      setIsSearching(true);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchFilters(searchFilters);
      setIsSearching(false);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchFilters]);

  // Call API when debounced filters change
  useEffect(() => {
    if (!isInitialMount.current) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchData();
      }
    } else {
      isInitialMount.current = false;
      prevCurrentPage.current = currentPage;
    }
  }, [debouncedSearchFilters]);

  // Call API when itemsPerPage changes
  useEffect(() => {
    if (!isInitialMount.current) {
      fetchData();
    }
  }, [itemsPerPage]);

  // Listen for genesis_import_complete event to refresh project list
  useEffect(() => {
    const handleGenesisImportComplete = (event) => {
      const entities = event?.detail?.entities;
      if (entities?.length && !entities.includes("licenses")) {
        return;
      }
      fetchData();
    };

    window.addEventListener('genesis_import_complete', handleGenesisImportComplete);
    return () => {
      window.removeEventListener('genesis_import_complete', handleGenesisImportComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetchData uses current state values, so we don't need to include dependencies

  const columns = ["licenseIdentifier", "licenseName", "licenseCategory", "homepageUrl", "copyleftFlag"];

  const columnConfig = {
    licenseIdentifier: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.license-identifier"),
      customRender: (value) => value || "---",
    },
    licenseName: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.license-name"),
      customRender: (value) => value || "---",
    },
    licenseCategory: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.license-category"),
      customRender: (value) => t(LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.find((opt) => opt.value === value)?.label) || "---",
    },
    homepageUrl: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.homepage-url"),
      customRender: (value) =>
        value ? (
          <div onClick={(event) => event.stopPropagation()}>
            <a href={value} target="_blank" rel="noopener noreferrer">
              <p className={colors.textActive}>{value}</p>
            </a>
          </div>
        ) : (
          <div>---</div>
        ),
    },
    copyleftFlag: {
      style: {
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.copyleft"),
      customRender: (value) => {
        const option = FLAG_OPTIONS.find((opt) => opt.value === value);
        return option && option.value === "Y" ? <MatchIcon /> : <UnMatchIcon />;
      },
    },
  };

  const handleDeleteLicense = () => {
    setShowDeleteModal(true);
  };

  const deleteLicenses = useMutationData(
    queryKeys.deleteLicenses,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        queryClient.invalidateQueries(queryKeys.getListLicenses);
      },
      onError: async (error) => {
        setShowDeleteModal(false);
        if (isNotNullAndEmptyArray(error.response.data?.deletedSeqs)) {
          setSelectedRows([]);
          showSuccessToast(error.response.data.deletedSeqs.length + t("open-source.license-delete-success"));
          fetchData();
        }
        console.error("Error during delete data:", error);
        if (error.response.status === 409) {
          showErrorToast(t("status.error"), error?.response?.data?.message + t("open-source.license-delete-error-conflict"));
        } else {
          showErrorToast(t("status.error"), t("open-source.license-delete-error"));
        }
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const bodyRequest = selectedRows;
      const selectedCount = selectedRows.length;
      await deleteLicenses.mutateAsync(bodyRequest, {
        onSuccess: () => {
          setSelectedRows([]);
          setShowDeleteModal(false);
          showSuccessToast(selectedCount + t("open-source.license-delete-success"));
        },
      });
    } catch (error) {
      console.error("Error deleting licenses:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddLicense = () => {
    navigate("./new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.licenseSeq}`);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const searchHandler = useCallback((inputData) => {
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  const handleLicenseCategoryChange = useCallback((selectedOption) => {
    const licenseCategory = selectedOption ? selectedOption.value : "";
    setSearchFilters(prev => ({
      ...prev,
      licenseCategory: licenseCategory
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="license-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder} py-1`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("open-source.license-list-title")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`}>
        <div className="mb-4 flex flex-row items-center justify-between">
          {/* Search section */}
          <div className="flex flex-row items-center space-x-4">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <MagnifyingGlassIcon className={`h-5 w-5 ${colors.textMuted}`} />
              </div>
              <input
                data-testid="license-list-search-input"
                type="text"
                placeholder={t("common.search-placeholder")}
                onChange={(e) => searchHandler({ searchText: e.target.value })}
                className={`block w-96 border py-2 pr-3 pl-10 h-10 ${colors.borderPrimary} rounded-md leading-5 ${colors.bgSecondary} ${colors.textPrimary} ${colors.borderHoverPrimary} ${colors.textActive} outline-none text-sm`}
              />
            </div>

            {/* License Category Select */}
            <div className="w-64" data-testid="license-list-category-select">
              <CustomSelect
                options={LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.map(option => ({
                  value: option.value,
                  label: t(option.label)
                }))}
                value={searchFilters.licenseCategory ? {
                  value: searchFilters.licenseCategory,
                  label: t(LICENSE_CATEGORY_OPTIONS_NO_TRANSLATION.find(opt => opt.value === searchFilters.licenseCategory)?.label || searchFilters.licenseCategory)
                } : null}
                onChange={handleLicenseCategoryChange}
                placeholder={t("open-source.license-category-placeholder")}
                size="md"
                className="w-full text-sm font-medium"
                clearable={true}
              />
            </div>
          </div>

          {/* Action buttons section */}
          <div className="flex flex-row items-center space-x-3">
            {hasDeletePermission && (
              <button
                type="button"
                data-testid="license-list-delete-button"
                onClick={handleDeleteLicense}
                disabled={selectedRows.length === 0}
                className={`inline-flex items-center justify-center rounded-md border px-4 py-2 h-10 text-sm font-medium whitespace-nowrap shadow-sm transition-colors ${colors.focusRing2} focus:ring-offset-2 focus:outline-none overflow-hidden ${selectedRows.length > 0
                  ? `bg-transparent ${colors.borderDangerOutLineButton} ${colors.textDanger}`
                  : `${colors.borderPrimary} ${colors.textSecondary} cursor-not-allowed opacity-50`
                  }`}
              >
                <span className="truncate" title={t("open-source.delete-license")}>
                  {t("open-source.delete-license")}
                </span>
              </button>
            )}

            <button
              data-testid="license-list-add-button"
              onClick={handleAddLicense}
              className={`font-600 inline-flex items-center justify-center rounded-md border-transparent px-4 py-2 h-10 text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors overflow-hidden`}
            >
              <span className="truncate" title={t("open-source.add-new-license")}>
                {t("open-source.add-new-license")}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* License Table */}
      <div className="flex-grow min-h-0" data-testid="license-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={licenseList}
          columns={columns}
          onRowClick={onRowClick}
          loading={getListData.isPending || isSearching}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="licenseSeq"
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="license-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("open-source.delete-licenses"))}
        description={t("common.delete-modal-body")}
        loading={deleteLoading}
      />
    </div>
  );
}
